import { Tooltip } from '@mantine/core';
import { NodeViewContent, NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

import { ActivityThemeStyles } from 'external-modules/ui';

import * as classes from './Tooltip.css';
import { TooltipConfig } from './TooltipConfig';
import { TooltipEditModal } from './TooltipEditModal';
import { SideMenuPortalBase, useContentObjectIsSelected } from '@/components/SideMenuPortal';
import { useCurrentDocument } from '@/components/YjsProvider';
import { useActivityThemeStylesProperty } from '@/contentObjects/Root/hooks/useActivityThemeStylesProperty';
import { getThemeBorderRadiusStyle, getThemeBorderStyle } from '@/utils/theme.utils';
import { useTooltipResolvedStyles } from '@/utils/tiptap/extensions/Tooltip/hooks/useTooltipResolvedStyles';
import { useWorkMode } from '@/utils/useWorkMode';
import { RichText } from '@/widgets/_components/RichText';

export function TooltipNode(props: NodeViewProps) {
  const {
    node: { attrs },
    updateAttributes,
    editor
  } = props;

  const [isOver, setIsOver] = useState(false);

  const { isStudioEdit } = useWorkMode();

  const [styles, setStyles] = useState<Partial<ActivityThemeStyles['tooltip']>>(attrs.tooltipStyle);

  const resolvedStyles = useTooltipResolvedStyles(styles);

  const handleUpdateStyle = useCallback(
    (styleToUpdate: Partial<ActivityThemeStyles['tooltip']>) => {
      setStyles(styles => ({ ...styles, ...styleToUpdate }));
    },
    [setStyles]
  );

  const handleRemoveStyle = useCallback(
    (styleKey: keyof ActivityThemeStyles['tooltip']) => {
      const { [styleKey]: _, ...restStyles } = styles;
      setStyles(restStyles);
    },
    [styles, setStyles]
  );

  useEffect(() => {
    queueMicrotask(() => {
      // Required (https://github.com/ueberdosis/tiptap/issues/3580)
      updateAttributes({ tooltipStyle: styles });
    });
  }, [styles, updateAttributes]);

  const onCloseEditModal = useCallback(
    (content: string) => {
      const emptyContentRegex = /^\s*<p[^>]*><\/p>\s*$/;
      const isEmptyContent = !content || emptyContentRegex.test(content);
      if (isEmptyContent) {
        return editor.commands.removeTooltip(attrs.tooltipId);
      }
      updateAttributes({ isEditMode: false, tooltipContent: content });
    },
    [attrs.tooltipId, editor.commands, updateAttributes]
  );

  const handleMouseEnter = useCallback(() => {
    setIsOver(true);
  }, [setIsOver]);

  const handleMouseLeave = useCallback(() => {
    setIsOver(false);
  }, [setIsOver]);

  const document = useCurrentDocument();
  const [borderColorFallback] = useActivityThemeStylesProperty<string>(
    document,
    'global.content.colors.decoration.main'
  );

  const [isActive, setIsActive] = useState(false);
  const isContentObjectSelected = useContentObjectIsSelected();

  useEffect(() => {
    if (!isContentObjectSelected) {
      setIsActive(false);
    }
  }, [isContentObjectSelected]);

  useEffect(() => {
    if (!isStudioEdit) return;

    const handleUpdate = () => {
      const isActive = editor.isActive('tooltip', { tooltipId: attrs.tooltipId });
      setIsActive(isActive);
    };

    editor.on('selectionUpdate', handleUpdate);
    return () => {
      editor.off('selectionUpdate', handleUpdate);
    };
  }, [attrs.tooltipId, editor, isStudioEdit]);

  return (
    <NodeViewWrapper as="span" className="react-component-with-content">
      <Tooltip
        classNames={{ tooltip: classes.tooltipLabel }}
        bg={resolvedStyles.backgroundColor}
        offset={resolvedStyles.offset}
        p={resolvedStyles.padding}
        styles={{
          tooltip: {
            ...getThemeBorderRadiusStyle(resolvedStyles.cornerRadius),
            ...(resolvedStyles.isBorderVisible ? getThemeBorderStyle(resolvedStyles.border, borderColorFallback) : {}),
            boxShadow: resolvedStyles.isShadowVisible ? resolvedStyles.shadow : 'none'
          }
        }}
        label={
          <div
            id={attrs.tooltipId}
            contentEditable={false}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleMouseEnter}
          >
            <RichText dataTestIdPrefix="tooltip" editable={false} htmlContent={attrs.tooltipContent} />
          </div>
        }
        opened={isActive || isOver}
        position={resolvedStyles.position}
        withinPortal={false}
        pos="fixed"
      >
        <span style={{ textDecoration: 'underline' }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <NodeViewContent as="span" className="content" aria-describedby={attrs.tooltipId} />
        </span>
      </Tooltip>

      {isStudioEdit && isActive && (
        <SideMenuPortalBase>
          <TooltipConfig
            onRemoveStyle={handleRemoveStyle}
            resolvedStyles={resolvedStyles}
            styles={styles}
            onUpdateStyle={handleUpdateStyle}
          />
        </SideMenuPortalBase>
      )}

      {isStudioEdit && attrs.isEditMode && (
        <TooltipEditModal onClose={onCloseEditModal} content={attrs.tooltipContent} />
      )}
    </NodeViewWrapper>
  );
}
